
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import intersection from 'lodash/intersection';

import { getModule } from '../../core';

import {
    sagas as sagasProject,
    utils as utilsProject,
    useToken
} from '../../project';

import appGetActions from '../saga';

import {
    checkProjectForLayout ,
    COMPONENT_NAME_MAP,
    COMPONENT_NAME_MAPBASIC,
    COMPONENT_NAME_MULTISCENE,
    COMPONENT_NAME_MULTISCENEBASIC,
    COMPONENT_NAME_MAPMULTISCENE,
    COMPONENT_NAME_MAPMULTISCENEBASIC,
    COMPONENT_NAME_SPHEREPHOTO,
    COMPONENT_NAME_SPHEREPHOTOBASIC
} from './appLayout';

import AccessDeniedPermission from './AccessDeniedPermission';
import AccessDeniedProject from './AccessDeniedProject';
import MapViewer from './MapViewer';
import MapBasicViewer from './MapBasicViewer';
import MultiSceneViewer from './MultiSceneViewer';
import MultiSceneBasicViewer from './MultiSceneBasicViewer';
import MapMultiSceneViewer from './MapMultiSceneViewer';
import MapMultiSceneBasicViewer from './MapMultiSceneBasicViewer';
import SpherePhotoViewer from './SpherePhotoViewer';
import SpherePhotoBasicViewer from './SpherePhotoBasicViewer';

import { Log } from '../../core';
const log = Log.getLogger('MultiSceneForApp.app.ProjectViewer');

/**
 * Common viewer for whole project workflow. It ensures reading, parsing and displaying
 * all content defined in project. Component for render project is determined on demand
 * by project's content.
 *
 * @param {Object} props
 * @param {string} props.project - project ID which will be used
 * @param {React.Component} [props.appLayoutComponent] - component for layout definition
 * @param {Object} [props.appLayoutProps] - properties for layout definition component
 * @param {Boolean} [props.anonymous] - specify if anonymous access is enabled or is required authorization
 */
const ProjectViewer = (props) => {

    const dispatch = useDispatch();
    const appActions = appGetActions(dispatch);
    const projectActions = sagasProject.getActions(dispatch);

    const token = useToken();
    const userProjectId = useSelector(state => {
        return state.auth.data ? state.auth.data.user.projectId : '';
    });
    const appProjectId = useSelector(state => {
        return state.app.projectId;
    });
    const urlParams = useSelector(state => {
        return state.app.urlParams;
    });
    const loading = useSelector(state => {
        return state.auth.loading;
    });
    const accessDenied = useSelector(state => {
        return state.project.project.accessDenied;
    });
    const projectDefinition = useSelector(state => {
        return state.project.project.project;
    });
    const map = useSelector(state => {
        return state.map.map;
    });
    const userRoles = useSelector(state => {
        return get(state, 'auth.data.user.roles', []);
    });
    const projectRoles = get(projectDefinition, 'roles', []);

    useEffect(() => {
        projectActions.setProjectParam('SERVER_API_URL', getModule('map').getServerApiUrl());

        utilsProject.readProject(
            token,
            props.project,
            projectActions
         );
    }, []);

    useEffect(() => {
        appActions.setUiValue(
            'AppLayoutComponent', getLayoutComponentName()
        );
    }, [props.appLayoutComponent, projectDefinition]);

    const getLayoutComponent = () => {
        let component = null;

        if (urlParams.appLayoutComponent) {
            component = getLayoutComponentFromName(urlParams.appLayoutComponent);
        } else {
            if (props.appLayoutComponent) {
                component = props.appLayoutComponent;
            } else {
                if (useMultiScene()) {
                    component = MapMultiSceneViewer;
                } else {
                    component = MapViewer;
                }
            }
        }
        return component;
    }

    const getLayoutComponentFromName = (name) => {
        switch (name) {
            case COMPONENT_NAME_MAP:
                return MapViewer;
            case COMPONENT_NAME_MAPBASIC:
                return MapBasicViewer
            case COMPONENT_NAME_MULTISCENE:
                return MultiSceneViewer;
            case COMPONENT_NAME_MULTISCENEBASIC:
                return MultiSceneBasicViewer;
            case COMPONENT_NAME_MAPMULTISCENE:
                return MapMultiSceneViewer;
            case COMPONENT_NAME_MAPMULTISCENEBASIC:
                return MapMultiSceneBasicViewer;
            case COMPONENT_NAME_SPHEREPHOTO:
                return SpherePhotoViewer;
            case COMPONENT_NAME_SPHEREPHOTOBASIC:
                return SpherePhotoBasicViewer;
            default:
                log.warn(`unknown layout component name: ${name}`);
                return null;
        }
    }

    const getLayoutComponentName = () => {
        let component = getLayoutComponent();
        if (component === MapViewer) {
            return COMPONENT_NAME_MAP;
        }
        if (component === MapBasicViewer) {
            return COMPONENT_NAME_MAPBASIC;
        }
        if (component === MultiSceneViewer) {
            return COMPONENT_NAME_MULTISCENE;
        }
        if (component === MultiSceneBasicViewer) {
            return COMPONENT_NAME_MULTISCENEBASIC;
        }
        if (component === MapMultiSceneViewer) {
            return COMPONENT_NAME_MAPMULTISCENE;
        }
        if (component === MapMultiSceneBasicViewer) {
            return COMPONENT_NAME_MAPMULTISCENEBASIC;
        }
        if (component === SpherePhotoViewer) {
            return COMPONENT_NAME_SPHEREPHOTO;
        }
        if (component === SpherePhotoBasicViewer) {
            return COMPONENT_NAME_SPHEREPHOTOBASIC;
        }
        log.warn(`unknown layout component: ${component.displayName}`);
        return '__UNKNOWN__';
    }

    const hasRequiredRole = () => {
        return (
            (intersection(userRoles, projectRoles).length > 0) &&
            (userProjectId === appProjectId)
        );
    }

    const isAnonymous = () => {
        return (props.anonymous === true);
    }


    const renderLayoutComponent = () => {
        const appLayoutProps = {
            appLayoutProps: props.appLayoutProps || {},
            map: map,
            projectDefinition: projectDefinition
        };

        return React.createElement(getLayoutComponent(), appLayoutProps);
    }

    const setProjectTitle = (title) => {
        let element = document.getElementById('projectTitle');
        if (element) {
            element.innerHTML = title;
        }
    }

    const useMultiScene = () => {
        return (
            projectDefinition &&
            projectDefinition.multiSceneProject &&
            (!!getModule('multiscene'))
        );
    }

    if ((!loading) && (projectDefinition && map.definition)) {
        if (isAnonymous() || hasRequiredRole()) {
            if (checkProjectForLayout(getLayoutComponentName(), projectDefinition)) {
                setProjectTitle(projectDefinition.title);
                return renderLayoutComponent();
            } else {
                return <AccessDeniedProject />
            }
        } else {
            return <AccessDeniedPermission />
        }
    } else {
        if (accessDenied) {
            return <AccessDeniedPermission onlyInfo={true} />
        } else {
            return <div>Nahrávám...</div>
        }
    }
}

export default ProjectViewer;
